import Swal from "sweetalert2";
import { Constants } from "./constants";
import { MedicalDept } from "@/enums/common.enums";
import dayjs from "dayjs";
import { BuildModeEnum } from "../enums/common.enums";

// 날짜 관련 utils
export const dateUtils = () => {
  return {
    unixTimeToYYYYMMDD(unixTime) {
      return dayjs(unixTime).format("YYYY-MM-DD");
    },
    YYYYMMDDtoUnixTime(dateString) {
      return dayjs(dateString, "YYYY-MM-DD").valueOf();
    },
    unixTimeToYYYYMMDDWithSlash(unixTime) {
      return dayjs(unixTime).format("YYYY/MM/DD");
    },
  };
};

// 날짜만 보여주기(스트링 잘라서)
export const convertDate = (dateString) => {
  if (dateString) {
    return dateString.split("T")[0].replaceAll("-", "/");
  } else {
    return `-`;
  }
};

// 로컬 시간으로 변환 (ISO 8601 형식 변환)
export const convertToLocalTime = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);

    // 날짜가 유효한지 확인
    if (isNaN(date.getTime())) {
      return "-";
    }

    // 로컬 시간 형식으로 변환 YYYY/MM/DD HH:mm:ss
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error("Date conversion error:", error);
    return "-";
  }
};

// 날짜와 시간 보여주기(스트링 잘라서)
export const convertDateTime = (dateString) => {
  if (dateString) {
    const date = dateString.split("T")[0].replaceAll("-", "/");
    const time = dateString.split("T")[1].split(".")[0];
    return `${date} ${time}`;
  } else {
    return `-`;
  }
};

export const formatInputToDate = (input) => {
  const onlyNumbers = input.replace(/\D/g, ""); // remove non-digits

  if (onlyNumbers.length <= 4) {
    return onlyNumbers;
  } else if (onlyNumbers.length <= 6) {
    let year = onlyNumbers.slice(0, 4);
    let month = onlyNumbers.slice(4);

    // Validate month
    if (parseInt(month, 10) > 12) {
      month = "12";
    }

    return `${year}/${month}`;
  } else {
    let year = onlyNumbers.slice(0, 4);
    let month = onlyNumbers.slice(4, 6);
    let day = onlyNumbers.slice(6, 8);

    // Validate month and day
    if (parseInt(month, 10) > 12) {
      month = "12";
    }
    if (parseInt(day, 10) > 31) {
      day = "31";
    }
    if (year === "0000") {
      year = dayjs().year();
    }
    if (month === "00") {
      month = "01";
    }
    if (day === "00") {
      day = "01";
    }

    return `${year}/${month}/${day}`;
  }
};

export const jwtDecoder = (jwtCode) => {
  try {
    if (jwtCode) {
      const base64Url = jwtCode.split(".")[1];
      // base64url을 표준 base64로 변환
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const companyId = JSON.parse(atob(base64))["sub"];
      return companyId;
    } else {
      return "";
    }
  } catch (error) {
    console.error("[jwtDecoder] error:", error);
    return "";
  }
};

export const changeBytesToMegabytes = (bytes) => {
  return bytes / 1024 / 1024;
};

// dialog 열기
export const oneBtnDialog = async (
  title,
  text,
  needsRefresh,
  confirmButtonText
) => {
  await Swal.fire({
    title: title,
    text: text,
    buttonsStyling: false,
    confirmButtonText: confirmButtonText ? confirmButtonText : "확인",
    heightAuto: true,
    customClass: {
      confirmButton: "btn btn-primary",
    },
  }).then(() => {
    if (needsRefresh) location.reload();
  });
};

export const oneBtnHtmlDialog = async (
  title,
  htmlText,
  needsRefresh,
  confirmBtnText
) => {
  await Swal.fire({
    title: title,
    html: htmlText,
    buttonsStyling: false,
    showCancelButton: false,
    heightAuto: true,
    confirmButtonText: confirmBtnText ? confirmBtnText : "확인",
    customClass: {
      confirmButton: "btn btn-primary",
    },
    reverseButtons: true,
  }).then(() => {
    if (needsRefresh) location.reload();
  });
};

// options.confirmStyle === "danger": 확인이 파괴적 동작(변경 버리기 등)일 때 확인을 경고형(흰 면·빨간 글자)으로,
// 취소(안전한 선택)를 강조형(노란 면)으로 바꾼다. 반환값 의미(확인 = true)는 그대로이며 Esc/바깥 클릭은 false(안전 쪽)다.
// options.focusCancel: 창이 뜰 때 포커스를 「취소」에(Enter 한 번에 되돌릴 수 없는 동작이 실행되지 않게)
export const twoBtnDialog = async (
  title,
  text,
  confirmBtnText,
  cancelBtnText,
  options = {}
) => {
  const danger = options.confirmStyle === "danger";
  const response = await Swal.fire({
    title: title,
    text: text,
    buttonsStyling: false,
    confirmButtonText: confirmBtnText ? confirmBtnText : "확인",
    cancelButtonText: cancelBtnText ? cancelBtnText : "취소",
    showCancelButton: true,
    heightAuto: true,
    customClass: {
      confirmButton: danger
        ? "btn btn-danger-confirm btn-hover swal-button-custom"
        : "btn btn-primary-confirm btn-hover swal-button-custom",
      cancelButton: danger
        ? "btn btn-primary-confirm btn-hover swal-button-custom"
        : "btn btn-secondary-cancel btn-hover swal-button-custom",
    },
    reverseButtons: true,
    focusCancel: !!options.focusCancel,
  });
  if (response) {
    const isConfirmed = response.isConfirmed;
    return isConfirmed;
  } else {
    return false;
  }
};

export const twoBtnHtmlDialog = async (title, htmlText, confirmBtnText) => {
  const response = await Swal.fire({
    title: title,
    html: htmlText,
    buttonsStyling: false,
    confirmButtonText: confirmBtnText ? confirmBtnText : "확인",
    cancelButtonText: "취소",
    showCancelButton: true,
    heightAuto: true,
    customClass: {
      confirmButton: "btn btn-primary-confirm btn-hover swal-button-custom",
      cancelButton: "btn btn-secondary-cancel btn-hover swal-button-custom",
    },
    reverseButtons: true,
  });
  if (response) {
    const isConfirmed = response.isConfirmed;
    return isConfirmed;
  } else {
    return false;
  }
};

export const formatCommaNumber = (value) => {
  if (value) return value.toLocaleString("en-US");
  else return value;
};

export const phoneNumberFormatter = (phoneNumber) => {
  if (!phoneNumber) return "";
  else {
    const value = phoneNumber.replace(/[^0-9]/g, "");
    return value.replace(
      /(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/,
      "$1-$2-$3"
    );
  }
};

export const businessNumberFormatter = (businessNumber) => {
  if (!businessNumber) return "";
  const value = businessNumber.replace(/[^0-9]/g, "");

  if (value.length <= 3) {
    return value;
  } else if (value.length <= 5) {
    return value.replace(/(\d{3})(\d{0,2})/, "$1-$2");
  } else {
    return value.replace(/(\d{3})(\d{2})(\d{0,5})/, "$1-$2-$3");
  }
};

export const decodeUtfText = (text) => {
  const byteArray = Uint8Array.from(text, (c) => c.charCodeAt(0));
  const decoder = new TextDecoder("utf-8");
  const decodedText = decoder.decode(byteArray);
  return decodedText;
};

export const decimalFormatter = (number) => {
  number = number.replace(/[^0-9.]/g, "");

  const dotCount = (number.match(/\./g) || []).length;
  if (dotCount > 1) {
    number = number.replace(/\./g, (match, offset) =>
      offset === number.indexOf(".") ? "." : ""
    );
  }

  const parts = number.split(".");
  if (parts.length > 2) {
    number = parts[0] + "." + parts[1];
  } else if (parts.length === 2 && parts[1].length > 2) {
    number = parts[0] + "." + parts[1].slice(0, 2);
  }

  return number;
};

export const getValueByTraitType = (attributes, targetValue) => {
  let value = "";
  if (attributes) {
    for (const item of attributes) {
      if (item.trait_type === targetValue) {
        value = item.value;
      }
    }
  }
  return value;
};

export const getValueByKeyName = (array, key) => {
  let value = "";
  for (const item of array) {
    if (item.key === key) {
      value = item.value;
      break;
    }
  }

  return value;
};

export const calcIsNew = (dateStr) => {
  const itemDate = dayjs(dateStr); // Parse the input date string
  const currentDate = dayjs(); // Get the current date

  const differenceInDays = currentDate.diff(itemDate, "day"); // Calculate the difference in days

  const daysThreshold = Constants.notice.numberOfDatesForNewPosts;
  return differenceInDays < daysThreshold;
};

export const convertDeptText = (input) => {
  switch (input) {
    case MedicalDept.DENT:
      return { certificateText: "인증서" };
    case MedicalDept.OPH:
      return { certificateText: "확인서" };
    default:
      return { certificateText: "인증서" };
  }
};

export const parseStringifiedObject = (object) => {
  return JSON.parse(JSON.stringify(object));
};

export const logging = () => {
  return {
    exceptProd: (txt, obj) => {
      if (process.env.NODE_ENV !== "production") {
        console.log(txt, obj);
      }
    },
  };
};

export const convertMonthAndYearText = (input) => {
  if (input < 12) return `${input}개월`;
  return `${input / 12}년`;
};

export const formatDate = () => {
  const formatting = (date) => {
    const d = dayjs(date);
    const year = d.year();
    const month = d.month() + 1;
    const day = d.date();
    return [
      year.toString(),
      month.toString().padStart(2, "0"),
      day.toString().padStart(2, "0"),
    ];
  };

  return {
    yyyymmdd: (date) => {
      return formatting(date).join("");
    },
    "yyyy-mm-dd": (date) => {
      return formatting(date).join("-");
    },
    unixTimeToYYYYMMDD: (unixTime) => {
      return dayjs.unix(unixTime / 1000).format("YYYYMMDD");
    },
    "sqlserverDateToyyyy/mm/dd": (dateString) => {
      return dayjs(dateString).format("YYYY/MM/DD");
    },
    getCurrentUnixTime: () => {
      return dayjs().valueOf();
    },
    getPastDatesInUnixTime: (value) => {
      const now = dayjs();
      let pastDate;

      switch (value) {
        case 3:
          pastDate = now.subtract(3, "month");
          break;
        case 12:
          pastDate = now.subtract(12, "month");
          break;
        case 36:
          pastDate = now.subtract(36, "month");
          break;
        default:
          pastDate = now;
      }

      return pastDate.valueOf();
    },
  };
};

export const compareUnixDates = (startUnix, endUnix) => {
  // Convert Unix timestamps to dayjs objects and set to midnight
  const startDate = dayjs(startUnix).startOf("day");
  const endDate = dayjs(endUnix).startOf("day");

  return startDate.valueOf() <= endDate.valueOf();
};

export const removeHyphen = (text) => {
  if (!text) return "";
  return text.replaceAll(/-/g, "");
};

export const convertProfile = () => {
  if (process.env.NODE_ENV === BuildModeEnum.LOCAL) return "local";
  if (process.env.NODE_ENV === BuildModeEnum.DEV) return "dev";
  if (process.env.NODE_ENV === BuildModeEnum.STG) return "stg";
  if (process.env.NODE_ENV === BuildModeEnum.PROD) return "prod";
  return "";
};

export const getAvailableSurgeryTypes = (companyInfo, dept) => {
  const deptObjs = companyInfo?.availableSurgeryTypes || [];
  const deptObj = deptObjs.find((item) => item.medicalDept === dept) || {};
  return deptObj.surgeryTypes || [];
};

export const isAvailableSurgeryType = (companyInfo, dept, targetType) => {
  const surgeryTypes = getAvailableSurgeryTypes(companyInfo, dept);
  return surgeryTypes.includes(targetType);
};

export const nextTickHandler = {
  wait(vm) {
    return new Promise((resolve) => {
      if (vm && vm.$nextTick) {
        vm.$nextTick(resolve);
      } else {
        setTimeout(resolve, 0);
      }
    });
  },
};

const localeMap = {
  zh: "zh-CN",
  ko: "ko-KR",
};

export const getLocaleByLanguageCode = (languageCode) => {
  return localeMap[languageCode] || "ko-KR";
};

// API 키 마스킹 처리 함수
export const maskApiKey = (apiKey) => {
  if (!apiKey || apiKey.length < 8) return apiKey;

  // API 키의 처음 3글자와 마지막 4글자만 보여주고 나머지는 * 처리
  const prefix = apiKey.substring(0, 3);
  const suffix = apiKey.substring(apiKey.length - 4);
  const maskedLength = Math.max(4, apiKey.length - 7); // 최소 4개의 *
  const masked = "*".repeat(maskedLength);

  return `${prefix}${masked}${suffix}`;
};

// API 키 유효성 검사 함수
export const isValidApiKey = (apiKey) => {
  // 빈 값이거나 마스킹된 값인 경우 유효하지 않음
  if (!apiKey || apiKey.includes("*")) {
    return false;
  }

  // 최소 길이 검증 (대부분의 API 키는 최소 20자 이상)
  return apiKey.length >= 20;
};
